import axios from 'axios';
import { getAdminTz } from './adminTime';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // FormData must go out as multipart with a browser-set boundary — the
  // instance-level JSON default would otherwise break file uploads.
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  // Admin reports bucket days/weeks/months in the admin's chosen timezone.
  if ((config.method || 'get').toLowerCase() === 'get' && String(config.url || '').startsWith('/api/admin')) {
    config.params = { tz: getAdminTz(), ...(config.params || {}) };
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 403 && err.response?.data?.detail === 'ACCOUNT_BANNED') {
      // Banned mid-session: drop the token and land on the login page with the suspension notice.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (!['/login', '/signup', '/verify-email'].includes(window.location.pathname)) {
        window.location.href = '/login?banned=1';
      }
      return Promise.reject(err);
    }
    if (err.response?.status === 403 && err.response?.data?.detail?.code === 'TRIAL_LOCKED') {
      // Trial ran out (or was never paid) mid-session: the dashboard route shows the lock wall.
      window.dispatchEvent(new CustomEvent('cmx:trial-locked', { detail: err.response.data.detail }));
      return Promise.reject(err);
    }
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        // Preserve the intended destination so login can send the user back
        // (critical after Stripe checkout returns to /subscription?session_id=…)
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/login?next=${next}`;
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Resolve a backend-returned media URL to an absolute URL the browser can
 * load. Absolute URLs (Azure Blob, external CDN) are returned as-is. Relative
 * paths like `/api/uploads/...` are prefixed with REACT_APP_BACKEND_URL so
 * the browser hits the backend directly instead of the current page origin
 * (which matters when dev-previewing across different hosts).
 */
export function resolveMediaUrl(u) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u) || /^blob:/i.test(u) || /^data:/i.test(u)) return u;
  const base = API_URL || '';
  return `${base}${u.startsWith('/') ? '' : '/'}${u}`;
}

export default api;
